import {
    ActionType,
    locales,
    Localization,
    Maybe,
    RegionId,
    rootRegionParentId,
    TenantRegion,
} from "@sp-crm/core";
import {
    GetEntityRegionPermissionsQuery,
    useGetEntityRegionPermissionsQuery,
} from "generated/graphql";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { stableQueryOptions } from "util/requests";
import { RegionState } from "../reducers/region";
import { ApplicationState } from "../state";
import { getRegion } from "./selectors";

export const usesMoreThanOneRegion = createSelector(getRegion, (region: RegionState) => {
    const { regions } = region;
    const selectableRegions = regions.filter(
        r => r.parentRegionId !== rootRegionParentId,
    );
    return selectableRegions.length > 1;
});

export const currentLocale = createSelector(
    getRegion,
    (region: RegionState): Localization => {
        const localeName = Maybe.fromValue(region.selectedRegion)
            .map(key => region.regions.find(r => r.key === key))
            .map(r => r.locale)
            .getOrElse("default");
        return locales[localeName] || locales.default;
    },
);

const noEntityRegions: GetEntityRegionPermissionsQuery["getEntityRegionPermissions"]["editableRegions"] =
    [];

interface UseEntityRegionsOptions {
    actionType?: ActionType;
    enabled?: boolean;
}

export const useEntityRegions = (options: UseEntityRegionsOptions = {}) => {
    const { actionType = ActionType.Noop, enabled = true } = options;

    const entityRegionPermissions = useGetEntityRegionPermissionsQuery(
        { params: { actionType } },
        {
            ...stableQueryOptions(),
            enabled,
        },
    );

    return (
        entityRegionPermissions?.data?.getEntityRegionPermissions?.editableRegions ??
        noEntityRegions
    );
};

export const excludeRootRegions = <T extends { id: RegionId }>(
    regions: T[],
    tenantRegions: Pick<TenantRegion, "id" | "parentRegionId">[],
): T[] => {
    const rootRegionIds = new Set(
        tenantRegions.filter(r => r.parentRegionId === rootRegionParentId).map(r => r.id),
    );
    return regions.filter(r => !rootRegionIds.has(r.id));
};

export const useEntityOfficeRegions = () => {
    const entityRegions = useEntityRegions();
    const tenantRegions = useSelector((state: ApplicationState) => state.region.regions);
    return useMemo(
        () => excludeRootRegions(entityRegions, tenantRegions),
        [entityRegions, tenantRegions],
    );
};

export const includeSelectedRegion = <T extends { id: RegionId }>(
    options: T[],
    allRegions: T[],
    selectedId: string | null | undefined,
): T[] => {
    if (options.some(r => r.id === selectedId)) {
        return options;
    }
    const selected = allRegions.find(r => r.id === selectedId);
    return selected ? [selected, ...options] : options;
};
